import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CTMapFiltrableProperties } from '@cloud-twin-v2/ct-map/models/ct-map-filtrable-properties';
import { CTMapNamespace } from '@cloud-twin-v2/ct-map/models/ct-map-namespace';
import { InstancesRegisterService } from '@cloud-twin-v2/generated/services/instances-register.service';
import {
  ElementTypeCategoriesDto,
  InstanceResponseDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { MasterOperatorService } from '@cloud-twin/master-operator/services/master-operator.service';
import { MapStep } from '@common-modules/wlm-map/models/map-step';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ScenarioItem } from '../scenario-item';

const COMPONENT_SELECTOR = 'tc-scenarios-list';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './scenarios-list.component.html',
  styleUrl: './scenarios-list.component.scss',
  standalone: false,
})
export class ScenariosListComponent implements OnInit {
  isPopupVisible = false;
  scenarios: ScenarioItem[];
  namespaces: Partial<CTMapFiltrableProperties>;

  private readonly _instanceRegisterService = inject(InstancesRegisterService);
  private readonly _masterOperatorService = inject(MasterOperatorService);

  private _connectSettings: WorkspaceConnectSettingDto[];

  public get connectSettings(): WorkspaceConnectSettingDto[] {
    return this._connectSettings;
  }

  @Input() public set connectSettings(value: WorkspaceConnectSettingDto[]) {
    this._connectSettings = value;
    if (Array.isArray(value) && value.length) {
      this.buildScenarios(value);
    } else {
      this.scenarios = [];
    }
  }

  @Input() attributesMetadata: Map<string, ElementTypeCategoriesDto[]>;
  @Input() step: MapStep;
  @Input() map;

  private _popupVisibilityChange: boolean;

  @Input()
  set popupVisibilityChange(value: boolean) {
    this._popupVisibilityChange = value;
    this.isPopupVisible = value;
    if (!value) {
      this.isPopupVisible = false;
    }
  }
  get popupVisibilityChange(): boolean {
    return this._popupVisibilityChange;
  }

  @Output() popupStateChange = new EventEmitter<boolean>();
  @Output() scenarioVisibilityChange = new EventEmitter<ScenarioItem>();
  @Output() namespacesChange = new EventEmitter<Partial<CTMapFiltrableProperties>>();

  showPopup(e) {
    e.stopPropagation();
    this.isPopupVisible = !this.isPopupVisible;
    this.popupStateChange.emit(this.isPopupVisible);
  }

  ngOnInit(): void {}

  onScenarioVisibilityChange(scenario: ScenarioItem): void {
    this.buildScenarioToNamespaceProperties(scenario);
    this.scenarioVisibilityChange.emit(scenario);
    this.onNamespacesChange(this.namespaces);
  }

  onNamespacesChange(namespaces: Partial<CTMapFiltrableProperties>): void {
    this.namespacesChange.emit(namespaces);
  }

  buildScenarioToNamespaceProperties(scenario: ScenarioItem) {
    const currentNamespaces = (this.namespaces?.namespaces ?? []) as CTMapNamespace[];

    if (scenario.isVisible === false) {
      this.namespaces = {
        namespaces: currentNamespaces.filter((ns) => ns.scenarioId !== scenario.scenarioId),
      };
    } else if (scenario.isVisible === true) {
      const exists = currentNamespaces.some((ns) => ns.scenarioId === scenario.scenarioId);
      if (!exists) {
        const newNamespace: WorkspaceConnectSettingDto = new WorkspaceConnectSettingDto({
          instanceId: scenario.instanceId,
          snapshotId: scenario.snapshotId,
          scenarioId: scenario.scenarioId,
        });
        this.namespaces = {
          namespaces: [...currentNamespaces, newNamespace as any],
        };
      }
    }
  }

  private buildScenarios(settings: WorkspaceConnectSettingDto[]): void {
    const instanceRequests = settings.map((setting) => {
      if (setting.instanceName === undefined) {
        return this.buildInstanceName(setting.instanceId).pipe(
          map((instanceName) => ({ ...setting, instanceName }))
        );
      } else {
        return of(setting);
      }
    });

    forkJoin(instanceRequests).subscribe((settingsWithNames) => {
      this.scenarios = settingsWithNames.map(
        (setting) =>
          ({
            scenarioId: setting.scenarioId,
            scenarioName: setting.scenarioName,
            instanceId: setting.instanceId,
            instanceName: setting.instanceName,
            snapshotId: setting.snapshotId,
            snapshotName: setting.snapshotName,
          } as ScenarioItem)
      );

      const namespaces = settingsWithNames.map(
        (setting) =>
          ({
            modelId: setting.instanceId,
            modelVersionId: setting.snapshotId,
            scenarioId: setting.scenarioId,
          } as CTMapNamespace)
      );

      this.namespaces = {
        namespaces: namespaces,
      } as Partial<CTMapFiltrableProperties>;

      this.namespacesChange.emit(this.namespaces);
    });
  }

  private buildInstanceName(instanceId: string): Observable<string> {
    return this._instanceRegisterService.getInstance(instanceId).pipe(
      map((instance) => {
        const name = this._masterOperatorService.instanceReadableLabel(
          instance as Required<InstanceResponseDto>
        );
        return name ?? '';
      }),
      catchError(() => of(''))
    );
  }
}
